import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { adminUserService, UserDetails as UserDetailsType, Role } from '@/services/adminUserService';
import { toastHelper } from '@/utils/toastHelper';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import UserDetailRole from './UserDetailRole';
import UserDetailSubscription from './UserDetailSubscription';
import UserDetailHistory from './UserDetailHistory';
import UserDetailTunes from './UserDetailTunes';

const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userDetails, setUserDetails] = useState<UserDetailsType | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize activeTab from URL query parameter, default to 'overview'
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Initialize tab from URL on mount and when searchParams change
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['overview', 'roles', 'subscriptions', 'history', 'tunes'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    } else {
      setActiveTab('overview');
    }
  }, [searchParams]);

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value && value !== 'overview') {
      setSearchParams({ tab: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const fetchUserDetails = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const data = await adminUserService.getUserDetails(userId);
      setUserDetails(data);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to fetch user details');
      navigate('/admin/user-management');
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  const fetchRoles = useCallback(async () => {
    try {
      const data = await adminUserService.getRoles();
      setRoles(data);
    } catch (error: any) {
      console.error('Failed to fetch roles:', error);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
      fetchRoles();
    }
  }, [userId, fetchUserDetails, fetchRoles]);


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">User not found</p>
        <Button onClick={() => navigate('/admin/user-management')} className="mt-4">
          Back to User Management
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-7xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            onClick={() => {
              // Check if we came from error logs
              const fromErrorLogs = (location.state as any)?.fromErrorLogs;
              if (fromErrorLogs) {
                navigate('/admin/error-logs');
              } else {
                navigate('/admin/user-management');
              }
            }}
            variant="ghost"
            size="sm"
            className="h-8 px-2 sm:px-4"
          >
            <ArrowLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-2xl font-bold truncate">User Details</h2>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">{userDetails.email}</p>
          </div>
        </div>
        <Button onClick={fetchUserDetails} variant="outline" size="sm" className="h-8 self-end sm:self-auto">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1 sm:gap-0 max-w-5xl h-auto p-1">
          <TabsTrigger value="overview" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Overview
          </TabsTrigger>
          <TabsTrigger value="roles" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Roles
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Subscriptions
          </TabsTrigger>
          <TabsTrigger value="history" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            History
          </TabsTrigger>
          <TabsTrigger value="tunes" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Tunes
          </TabsTrigger>
        </TabsList>

        <div className="w-full">
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>User Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{userDetails.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">User ID</Label>
                  <p className="font-mono text-xs">{userDetails.id}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="flex gap-2 mt-1">
                    <Badge variant={userDetails.is_active ? "default" : "secondary"}>
                      {userDetails.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {userDetails.is_complimentary && (
                      <Badge variant="outline">Complimentary</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date Created</Label>
                  <p>{userDetails.date_created ? new Date(userDetails.date_created).toLocaleDateString() : 'N/A'}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Subscription Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <Label className="text-muted-foreground">Tier</Label>
                  <Badge variant="outline" className="mt-1">
                    {userDetails.subscription_tier || 'free'}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <p>{userDetails.subscription_status || 'active'}</p>
                </div>
                {userDetails.is_complimentary && userDetails.complimentary_reason && (
                  <div>
                    <Label className="text-muted-foreground">Complimentary Reason</Label>
                    <p>{userDetails.complimentary_reason}</p>
                  </div>
                )}
                {userDetails.subscription_start_date && (
                  <div>
                    <Label className="text-muted-foreground">Start Date</Label>
                    <p>{new Date(userDetails.subscription_start_date).toLocaleDateString()}</p>
                  </div>
                )}
                {userDetails.next_billing_date && (
                  <div>
                    <Label className="text-muted-foreground">Next Billing Date</Label>
                    <p>{new Date(userDetails.next_billing_date).toLocaleDateString()}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          </TabsContent>

          <TabsContent value="roles" className="space-y-4">
            <UserDetailRole 
              userDetails={userDetails} 
              roles={roles} 
              onRefresh={fetchUserDetails}
            />
          </TabsContent>

          <TabsContent value="subscriptions" className="space-y-4">
            <UserDetailSubscription 
              userDetails={userDetails} 
              onRefresh={fetchUserDetails}
            />
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <UserDetailHistory userId={userId!} />
          </TabsContent>

          <TabsContent value="tunes" className="space-y-4">
            <UserDetailTunes userId={userId!} />
          </TabsContent>
        </div>
      </Tabs>


    </div>
  );
};

export default UserDetails;




